import { Component, type ErrorInfo, type ReactNode } from 'react';
import logoShape from '@/assets/images/logoShape.png';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  mode?: 'fullscreen' | 'inline';
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Auto-reload on stale dynamic import / chunk errors if not reloaded recently
    const isModuleError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('error loading dynamically imported module') ||
      error?.message?.includes('Importing a module script failed') ||
      error?.name === 'ChunkLoadError';

    if (isModuleError && typeof window !== 'undefined') {
      const reloadKey = 'dv_boundary_reload_ts';
      const lastReload = Number(sessionStorage.getItem(reloadKey) || 0);
      const now = Date.now();
      if (now - lastReload > 15000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
      }
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isInline = this.props.mode === 'inline';

      if (isInline) {
        return (
          <div
            id="error-boundary-inline"
            className="w-full min-h-[350px] flex items-center justify-center p-6"
            dir="rtl"
          >
            <div className="max-w-md w-full bg-surface-container-lowest border border-outline-variant rounded-2xl p-6 shadow-soft flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-error-container text-on-error-container flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-2xl">error</span>
              </div>

              <h2 className="text-base font-bold text-on-surface mb-2">
                تعذّر تحميل محتوى الصفحة
              </h2>

              <p className="text-xs text-on-surface-variant mb-4 leading-relaxed">
                حدث خطأ أثناء تحميل مكونات هذه الصفحة. يمكنك المحاولة مجدداً أو إعادة تحميل الصفحة.
              </p>

              {this.state.error?.message && (
                <div className="w-full bg-surface-container rounded-lg p-2.5 text-start mb-4 border border-outline-variant text-[11px] font-mono text-error break-words max-h-24 overflow-y-auto">
                  {this.state.error.message}
                </div>
              )}

              <div className="flex items-center gap-3 w-full">
                <button
                  type="button"
                  id="error-boundary-retry-btn"
                  onClick={this.handleReset}
                  className="flex-1 h-[38px] px-4 rounded-xl bg-primary hover:bg-primary/90 text-on-primary font-medium text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer shadow-sm"
                >
                  <span className="material-symbols-outlined text-[16px]">refresh</span>
                  المحاولة مجدداً
                </button>

                <button
                  type="button"
                  id="error-boundary-reload-btn"
                  onClick={this.handleReload}
                  className="h-[38px] px-3.5 rounded-xl border border-outline-variant hover:bg-surface-container text-on-surface font-medium text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                >
                  إعادة التحميل
                </button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          id="error-boundary-screen"
          className="min-h-screen w-full flex items-center justify-center p-6 bg-slate-900 text-slate-100"
          dir="rtl"
        >
          <div className="max-w-md w-full bg-slate-800/80 border border-slate-700/80 rounded-2xl p-6 md:p-8 shadow-2xl flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5">
              <img src={logoShape} alt="Deep Vision ERP" className="w-10 h-10 object-contain" />
            </div>

            <h1 className="text-xl font-bold text-white mb-2">
              حدث خطأ غير متوقع
            </h1>

            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              واجه التطبيق مشكلة أثناء معالجة الصفحة. يرجى إعادة تحميل التطبيق للمتابعة.
            </p>

            {this.state.error?.message && (
              <div className="w-full bg-slate-950/60 rounded-lg p-3 text-start mb-6 border border-slate-800 text-xs font-mono text-rose-300 break-words max-h-32 overflow-y-auto">
                {this.state.error.message}
              </div>
            )}

            <div className="flex items-center gap-3 w-full">
              <button
                type="button"
                id="error-boundary-reload-btn"
                onClick={this.handleReload}
                className="flex-1 h-[42px] px-4 rounded-xl bg-primary hover:bg-primary/90 text-on-primary font-medium text-sm flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-lg"
              >
                <span className="material-symbols-outlined text-[18px]">refresh</span>
                إعادة تحميل الصفحة
              </button>

              <button
                type="button"
                id="error-boundary-reset-btn"
                onClick={this.handleReset}
                className="h-[42px] px-4 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium text-sm flex items-center justify-center gap-2 transition-colors cursor-pointer"
              >
                المحاولة مجدداً
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
